import { MessageRequiredAnswer } from '../../../models/messageRequiredAnswer';
import { MutationDefinition, QueryDefinition } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { store } from '../../../app/store';
import { AdmEnhet, AdmEnhetLight, BrukerLight, EnkelBruker, EnkelIdentitet, Organisering, PaginatedResult, } from '../../../models';
import { FraTilAvdelingNode } from '../../../models/fraTilAvdelingNode';
import { client } from '../../client';
import { admEnhetLightService, admEnhetService, brukerService, identitetService, } from '../../services';
import { getNextPageURL } from '../../services/requestService';
import { FetchArg, FilterSearchArg, PageParam, TBaseQueryFn, TEntityDefinitions } from '../queryApiCreator';
import { provideTagList } from '../queryApiService';
import { TreeQueryApiService } from '../queryApiTreeCreator';

interface AdmEnhetDefinitions extends TEntityDefinitions<AdmEnhet, typeof admEnhetService.typeName> {
  brukereByAdmEnhet: QueryDefinition<{ admEnhetId: AdmEnhet['id'], fetchParam: FetchArg<BrukerLight> }, TBaseQueryFn<BrukerLight>, any, PaginatedResult<BrukerLight>, string>;
  enkelBrukereByAdmEnhet: QueryDefinition<{ admEnhetId: string } & FilterSearchArg & PageParam, TBaseQueryFn<EnkelBruker>, any, PaginatedResult<EnkelBruker>, string>;
  identitetByAdmEnhet: QueryDefinition<{ admEnhetId: string } & FilterSearchArg & PageParam, TBaseQueryFn<EnkelIdentitet>, any, PaginatedResult<EnkelIdentitet>, string>;
  admEnhetLight: QueryDefinition<FetchArg<AdmEnhetLight>, TBaseQueryFn<AdmEnhetLight>, any, PaginatedResult<AdmEnhetLight>, string>;
  skalOverforingUtforesMelding: QueryDefinition<FraTilAvdelingNode, TBaseQueryFn<any>, any, MessageRequiredAnswer, string>;
  overforeAdminEnhet: MutationDefinition<FraTilAvdelingNode, TBaseQueryFn<any>, any, string, string>;
}

class AdmEnhetQueryApiService extends TreeQueryApiService<AdmEnhet, typeof admEnhetService.typeName, AdmEnhetDefinitions> {
  readonly ENHET_LIGHT_TAG = `${this.serviceName}_light`;
  constructor() {
    super(admEnhetService);

    this.api
      .enhanceEndpoints({
        addTagTypes: [this.serviceName, this.ENHET_LIGHT_TAG],
      }).injectEndpoints({
        endpoints: (build) => {
          return {
            brukereByAdmEnhet: build.query<PaginatedResult<BrukerLight>, { admEnhetId: AdmEnhet['id'], fetchParam: FetchArg<BrukerLight> }>({
              query: ({ admEnhetId, fetchParam }: { admEnhetId: AdmEnhet['id'], fetchParam: FetchArg<BrukerLight> }) => {
                const pageUrl = fetchParam.param ? getNextPageURL({ endpoint: `${this.serviceName}/${admEnhetId}/brukere`, param: fetchParam.param }) : undefined;
                return {
                  request: client.get<PaginatedResult<BrukerLight>>(pageUrl ?? `${this.serviceName}/${admEnhetId}/brukere`),
                };
              },
              providesTags: (result) => provideTagList(result?.value, brukerService.typeName)
            }),
            enkelBrukereByAdmEnhet: build.query<PaginatedResult<EnkelBruker>, { admEnhetId: string } & FilterSearchArg & PageParam>({
              query: (arg) => { return ({ request: admEnhetService.enkelBrukereByAdmEnhet(arg.admEnhetId, arg.query, { skip: arg.skip, top: arg.top }, arg.filter) }); },

              providesTags: (result) => provideTagList(result?.value, brukerService.typeName)
            }),
            identitetByAdmEnhet: build.query<PaginatedResult<EnkelIdentitet>, { admEnhetId: string } & FilterSearchArg & PageParam>({
              query: arg => { return ({ request: admEnhetService.enkelIdentitetByAdmEnhet(arg.admEnhetId, arg.query, { skip: arg.skip, top: arg.top }, arg.filter) }); },
              providesTags: (result) => provideTagList(result?.value, identitetService.typeName)
            }),

            admEnhetLight: build.query<PaginatedResult<AdmEnhetLight>, FetchArg<AdmEnhetLight>>({
              query: (arg) => { return { request: admEnhetService.admEnheterLight(arg) }; },
              providesTags: (result) => provideTagList(result?.value, this.ENHET_LIGHT_TAG)
            }),
            overforeAdminEnhet: build.mutation<string, FraTilAvdelingNode>({
              query: (overforeAdminEnhet: FraTilAvdelingNode) => ({ request: admEnhetService.overforeAdminEnhet(overforeAdminEnhet) }),
            }),
            skalOverforingUtforesMelding: build.query<MessageRequiredAnswer, FraTilAvdelingNode>({
              query: (overforeAdminEnhet: FraTilAvdelingNode) => ({ request: admEnhetService.skalOverforingUtforesMelding(overforeAdminEnhet) }),
            }),
          };
        },
        overrideExisting: true
      });
  }

  get admEnheterLightOptionsPlainSearch() {
    return async (searchString: string, predicate?: (value: AdmEnhetLight) => boolean) => {
      const result = (await store.dispatch(this.api.endpoints.admEnhetLight.initiate({ query: searchString }))).data?.value;
      return predicate && result ? result.filter(predicate) : result ?? [];
    };
  }


  get optionsPaginatedFilterBrukerSearch() {
    return async (admEnhetId: string, searchArg: FilterSearchArg, { skip = 0, top = 20 }: PageParam, predicate?: (value: EnkelBruker) => boolean, forceRefetch?: boolean) => {
      const result = (await store.dispatch(this.api.endpoints.enkelBrukereByAdmEnhet.initiate({ admEnhetId, ...searchArg, skip, top }, { subscribe: !forceRefetch, forceRefetch }))).data;
      return predicate && result ? { ...result, value: result.value.filter(predicate) } : result;
    };
  }
  get optionsPaginatedIdentitetByAdmEnhetSearch() {
    return async (admEnhetId: string, searchArg: FilterSearchArg, { skip = 0, top = 20 }: PageParam, predicate?: (value: EnkelIdentitet) => boolean, forceRefetch?: boolean) => {
      const result = (await store.dispatch(this.api.endpoints.identitetByAdmEnhet.initiate({ admEnhetId, ...searchArg, skip, top }, { subscribe: !forceRefetch, forceRefetch }))).data;
      return predicate && result ? { ...result, value: result.value.filter(predicate) } : result;
    };
  }

}

export const admEnhetQueryApiService = new AdmEnhetQueryApiService();
